'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import type { AsyncResult, AppError } from '@shared/utils';
import { tryCatch, tryCatchAsync, err, ok } from '@shared/utils';
import { Logger } from '@shared/logger';
import type { ScannerWebSocketMessage } from '@/src/types/ScannerWebSocketMessage';

function normalizeWebSocketUrl(wsUrl: string): string {
  if (typeof window === 'undefined') {
    return wsUrl;
  }
  const isSecure = window.location.protocol === 'https:';
  const url = new URL(wsUrl);
  url.protocol = isSecure ? 'wss:' : 'ws:';
  return url.toString();
}

/**
 * @group Hooks
 * @summary
 * <blockquote>Hook for managing WebSocket connection to scanner API and handling messages</blockquote>
 * 
 * @example
 * ```tsx
 * const { connect, disconnect, isConnected, lastMessage, sendFrame } = useScannerWebSocket({
 *   onStatus: (msg) => console.log('Status:', msg),
 *   onFinished: (msg) => console.log('Finished:', msg),
 *   onError: (msg) => console.error('Error:', msg),
 * });
 * 
 * const handleStart = async () => {
 *   // wsUrl from BFF already contains token in query string
 *   const result = await connect(session.wsUrl);
 *   result.match({
 *     ok: () => console.log('Connected'),
 *     err: (e) => console.error(e.errorMessage),
 *   });
 * };
 * ```
 */
interface UseScannerWebSocketOptions {
  onStatus?: (message: Extract<ScannerWebSocketMessage, { type: 'status' }>) => void;
  onFinished?: (message: Extract<ScannerWebSocketMessage, { type: 'finished' }>) => void;
  onError?: (message: Extract<ScannerWebSocketMessage, { type: 'error' }>) => void;
}

interface UseScannerWebSocketReturn {
  connect: (wsUrl: string) => AsyncResult<void, AppError>;
  disconnect: () => void;
  isConnected: boolean;
  lastMessage: ScannerWebSocketMessage | null;
  sendFrame: (buffer: ArrayBuffer) => void;
}

export function useScannerWebSocket(
  options?: UseScannerWebSocketOptions
): UseScannerWebSocketReturn {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<ScannerWebSocketMessage | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  const onStatusRef = useRef(options?.onStatus);
  const onFinishedRef = useRef(options?.onFinished);
  const onErrorRef = useRef(options?.onError);
  onStatusRef.current = options?.onStatus;
  onFinishedRef.current = options?.onFinished;
  onErrorRef.current = options?.onError;

  const connect = useCallback(async (wsUrl: string): AsyncResult<void, AppError> => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    const normalizedUrl = normalizeWebSocketUrl(wsUrl);

    const result = await tryCatchAsync(async () => {
      const ws = new WebSocket(normalizedUrl);
      ws.binaryType = 'arraybuffer';

      return new Promise<WebSocket>((resolve, reject) => {
        ws.onopen = () => {
          setIsConnected(true);
          wsRef.current = ws;
          Logger.info('WebSocket connected', {
            resource: 'scanner.websocket',
          });
          resolve(ws);
        };

        ws.onerror = (error) => {
          Logger.error('WebSocket error', {
            resource: 'scanner.websocket',
          }, error instanceof Error ? error : new Error('WebSocket connection error'));
          reject(new Error('WebSocket connection failed'));
        };

        ws.onclose = (event) => {
          setIsConnected(false);
          if (wsRef.current === ws) {
            wsRef.current = null;
          }
          Logger.info('WebSocket closed', {
            resource: 'scanner.websocket',
            code: event.code,
            reason: event.reason,
          });
        };

        ws.onmessage = (event) => {
          const parseResult = tryCatch(() => JSON.parse(event.data) as ScannerWebSocketMessage);

          if (parseResult.isErr) {
            Logger.error('Failed to parse WebSocket message', {
              resource: 'scanner.websocket',
            }, new Error(String(parseResult.error)));
            return;
          }

          const msg = parseResult.value;
          setLastMessage(msg);

          if (msg.type === 'status') {
            onStatusRef.current?.(msg);
          } else if (msg.type === 'finished') {
            onFinishedRef.current?.(msg);
          } else if (msg.type === 'error') {
            onErrorRef.current?.(msg);
            Logger.error('WebSocket error message', {
              resource: 'scanner.websocket',
              errorCode: msg.code,
            }, new Error(msg.message));
          }
        };
      });
    });

    return result.match({
      ok: async () => ok(undefined),
      err: async (e) => {
        const appError: AppError = {
          errorCode: 'WEBSOCKET_CONNECTION_FAILED',
          errorMessage: e instanceof Error ? e.message : 'Failed to connect WebSocket',
        };
        Logger.error('WebSocket connection failed', {
          resource: 'scanner.websocket',
          errorCode: appError.errorCode,
        }, e instanceof Error ? e : new Error(appError.errorMessage));
        return err(appError);
      },
    });
  }, []);

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
      setIsConnected(false);
      setLastMessage(null);
      Logger.info('WebSocket disconnected', {
        resource: 'scanner.websocket',
      });
    }
  }, []);

  const sendFrame = useCallback((buffer: ArrayBuffer) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(buffer);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);

  return {
    connect,
    disconnect,
    isConnected,
    lastMessage,
    sendFrame,
  };
}
